import React, { FC, useEffect, useState } from 'react'
import { Flex, Skeleton, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { Button, EmptyState, PushlyAi, Well } from '@pushly/aqe/lib/components'
import { useService } from '@pushly/aqe/lib/hooks'
import { AiGenerationDirective } from '@pushly/models/lib/structs/pushly-ai/ai-generation-directive'
import { AI_VIEW_LABELS, AiView, domainHasFlag } from './constants'
import { BrandVoiceStatus, type BrandVoiceState } from './brand-voice/brand-voice-status'
import { PUSHLY_AI_DOCS_URL } from './brand-voice/brand-voice-help-content'
import { tierCounts } from './brand-voice/brand-voice-tier-visual'
import { AiGenerationDirectiveService } from '../../services'
import { FEAT_INTEGRATIONS_VESPA_REC, FEAT_PUSHLY_AI } from '../../constants'
import { DomainDto } from '../../dtos/domain'
import { VERTICAL_WELL_GAP } from '../../theme/constants'
import { getClassNames } from '../../_utils/classnames'

dayjs.extend(relativeTime)

interface IAiOverview {
    loading: boolean
    domain?: DomainDto
    availableViews: AiView[]
    onViewChange: (view: AiView) => void
}

interface IFeatureSummary {
    view: AiView
    description: string
    /** Absent for a service that is always available to whoever can see it. */
    flag?: string
    internalOnly?: boolean
}

const FEATURES: IFeatureSummary[] = [
    {
        view: AiView.BRAND_VOICE,
        flag: FEAT_PUSHLY_AI,
        description:
            'Write your editorial standards once, in your own words, and every title and line of copy the model ' +
            'produces follows them. Rules we can check automatically are enforced on every generation; the rest ' +
            'guides tone, so what goes out sounds like your team rather than a generic assistant.',
    },
    {
        view: AiView.CURATOR,
        flag: FEAT_INTEGRATIONS_VESPA_REC,
        description:
            'Every audience gets the story it is most likely to open, chosen from your own library and scored ' +
            'against what that segment has actually clicked. Set it once on a journey step and each send picks ' +
            'itself, so one campaign lands differently for readers with different interests.',
    },
    {
        view: AiView.PAGE_ANALYSIS,
        description:
            'Reads the pages your audience opens and turns them into something the rest of the suite can use: ' +
            'clean article text, a classification, keywords and imagery. It is what Brand Voice writes from and ' +
            'what the Curator ranks, so the wider the coverage, the better everything downstream gets.',
        internalOnly: true,
    },
]

/**
 * The domain's Brand Voice, read for its card: undefined while it loads, null when none is set up. Not read without
 * Pushly AI, since there is nothing to read then.
 */
const useBrandVoice = (domainId: number | undefined, enabled: boolean): AiGenerationDirective | null | undefined => {
    const directiveSvc = useService(AiGenerationDirectiveService)
    const [directive, setDirective] = useState<AiGenerationDirective | null | undefined>(undefined)

    useEffect(() => {
        if (!domainId || !enabled) {
            setDirective(undefined)
            return
        }

        let cancelled = false
        setDirective(undefined)
        directiveSvc.fetchBrandVoice(domainId).then((res) => {
            if (!cancelled) setDirective(res.ok ? res.data ?? null : null)
        })

        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domainId, enabled])

    return directive
}

/** What the domain's Brand Voice holds, in one line: its rules by tier, and when it was last edited. */
const brandVoiceSummary = (directive: AiGenerationDirective): string => {
    const compiled = directive.active_version?.compiled_rules ?? null
    const counts = tierCounts(compiled)
    // A Brand Voice saved before its rules were stored has none to count, which is not the same as having none.
    const rules = compiled
        ? `${counts.guaranteed} enforced ${counts.guaranteed === 1 ? 'rule' : 'rules'}, ${counts.guidance} guidance`
        : undefined
    const edited = directive.updated_at ?? directive.created_at
    const editedText = edited ? `Edited ${dayjs(edited).fromNow()}` : undefined

    return [rules, editedText].filter(Boolean).join(' · ')
}

/**
 * The index for the section. It is deliberately a summary rather than a menu:
 * the services share one pipeline, so the thing worth seeing on arrival is
 * which of them are on and what they are currently doing.
 */
export const AiOverview: FC<IAiOverview> = ({ loading, domain, availableViews, onViewChange }) => {
    const suiteEnabled = domainHasFlag(domain, FEAT_PUSHLY_AI)
    const brandVoice = useBrandVoice(domain?.id, suiteEnabled)
    // Without Pushly AI there is no status: the notice above already says what Brand Voice needs.
    const brandVoiceState: BrandVoiceState | undefined = !suiteEnabled ? undefined : brandVoice ? 'live' : 'notSetUp'

    return (
        <Flex vertical gap={VERTICAL_WELL_GAP} className={getClassNames('ai-overview')}>
            <Well
                title={
                    <>
                        <PushlyAi /> Pushly AI
                    </>
                }
                footerMode="hidden"
            >
                <Skeleton loading={loading} active={true} title={false}>
                    <Typography.Paragraph className="ai-muted-text">
                        Pushly AI is a suite of services that read your published content and use it to write and target
                        your notifications. It generates titles, copy and imagery, recommends the article each audience
                        is most likely to click, and holds all of it to a brand voice you define. Each service below has
                        its own settings and its own record of what it did. It never touches content you write yourself.
                    </Typography.Paragraph>
                </Skeleton>
            </Well>

            {/*
             * Without the suite flag the section still shows each service, so a domain can see what Pushly AI
             * would do for it. This says once what it needs and where to read more, rather than on every service.
             */}
            {!loading && !suiteEnabled && (
                <Well hideHeader={true} footerMode="hidden">
                    <EmptyState
                        icon={<PushlyAi />}
                        iconVariant="chip"
                        title="Pushly AI is off for this domain"
                        description={
                            <>
                                When it is on, Pushly AI writes titles and copy from your published content and holds
                                them to a Brand Voice you define. Ask your Pushly account manager how it can work for
                                your content.{' '}
                                <a href={PUSHLY_AI_DOCS_URL} target="_blank" rel="noopener noreferrer">
                                    Learn more
                                </a>
                            </>
                        }
                    />
                </Well>
            )}

            {FEATURES.filter((feature) => availableViews.includes(feature.view)).map((feature) => {
                const featureEnabled = !feature.flag || domainHasFlag(domain, feature.flag)
                const isBrandVoice = feature.view === AiView.BRAND_VOICE

                return (
                    <Well
                        key={feature.view}
                        title={
                            <Flex align="center" gap={8}>
                                {AI_VIEW_LABELS[feature.view]}
                                {feature.internalOnly && <Tag className="ai-title-tag">Internal only</Tag>}
                            </Flex>
                        }
                        actions={
                            <Flex align="center" gap={8}>
                                {isBrandVoice
                                    ? !loading && brandVoiceState && <BrandVoiceStatus state={brandVoiceState} />
                                    : !featureEnabled && <Tag className="ai-title-tag">Not enabled</Tag>}
                                <Button size="small" onClick={() => onViewChange(feature.view)}>
                                    {isBrandVoice && brandVoiceState === 'notSetUp' ? 'Set up' : 'Open'}
                                </Button>
                            </Flex>
                        }
                        footerMode="hidden"
                    >
                        <Skeleton loading={loading} active={true} title={false}>
                            <Typography.Paragraph className="ai-muted-text" style={{ marginBottom: 0 }}>
                                {feature.description}
                            </Typography.Paragraph>
                            {/* What the Brand Voice holds now, so the overview says what it is doing, not only what
                                it is for. */}
                            {isBrandVoice && brandVoice && (
                                <Typography.Text className={getClassNames('ai-overview-summary')}>
                                    {brandVoiceSummary(brandVoice)}
                                </Typography.Text>
                            )}
                        </Skeleton>
                    </Well>
                )
            })}
        </Flex>
    )
}
